import { LinkedinLogo } from "@phosphor-icons/react";
import { useMemo } from "react";

import { DataTable } from "common/components/DataTable";
import useDataTable from "common/components/DataTable/useDataTable";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";

import { NEW_CAMPAIGN_AUDIENCE_COLUMNS } from "../../../../../constants";
import AudienceTargetActions from "../AudienceTargetActions";

import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { AudienceFilters, AudienceProfile } from "../../../../../types";

interface AudienceDesktopTableProps {
  rows: AudienceProfile[];
  isLoading: boolean;
  pageSize: number;
  totalItemCount: number;
  selectedProfiles: AudienceProfile[];
  setSelectedProfiles: Dispatch<SetStateAction<AudienceProfile[]>>;
  openLeadPreview: (leadId: string) => void;
  emptyStateComponent: ReactNode;
  filters: AudienceFilters;
  columnVisibility: VisibilityState;
  setColumnVisibility: (columnVisibility: VisibilityState) => void;
}

export default function AudienceDesktopTable({
  rows,
  isLoading,
  pageSize,
  totalItemCount,
  selectedProfiles,
  setSelectedProfiles,
  openLeadPreview,
  emptyStateComponent,
  filters,
  columnVisibility,
  setColumnVisibility,
}: AudienceDesktopTableProps) {
  const { permissions } = useSelectedWorkspaceContext();
  const canOpenLeadPreview = permissions.includes("leads_full");

  const columns = useMemo<ColumnDef<AudienceProfile>[]>(
    () => [
      {
        id: "target",
        header: "Target",
        cell: ({ row: { original } }) => {
          const {
            picture_url: pictureUrl,
            full_name: fullName,
            premium,
          } = original;

          return (
            <div className="flex items-center gap-x-2.5">
              {isLoading ? (
                <Skeleton className="size-10 rounded-full" />
              ) : (
                <ProfileImage src={pictureUrl} />
              )}
              <div className="flex flex-col gap-y-0.5">
                {isLoading ? (
                  <>
                    <Skeleton className="h-5 w-28" />
                    <Skeleton className="h-4 w-44" />
                  </>
                ) : (
                  <div className="flex items-center gap-x-1">
                    <span className="line-clamp-1 break-all text-xs-bold">
                      {fullName}
                    </span>

                    {premium && (
                      <LinkedinLogo
                        className="size-4 text-yellow-500"
                        weight="fill"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        },
        enableHiding: false,
      },
      ...typedObjectEntries(NEW_CAMPAIGN_AUDIENCE_COLUMNS).map(
        ([id, { label, icon, renderTag }]): ColumnDef<AudienceProfile> => ({
          id,
          header: label,
          cell: ({ row: { original } }) => {
            if (isLoading) {
              return (
                <Skeleton
                  className={
                    id === "occupation" || id === "location"
                      ? "h-4 w-24"
                      : "h-4 w-32"
                  }
                />
              );
            }
            return renderTag(original, true);
          },
          meta: { icon, className: "min-w-40 max-w-40" },
        }),
      ),
      {
        id: "action",
        cell: ({ row: { original } }) => {
          if (isLoading) {
            return <Skeleton className="inline-flex size-8 rounded-full" />;
          }

          return <AudienceTargetActions profile={original} filters={filters} />;
        },
        enableHiding: false,
        meta: { className: "w-0", isSticky: true },
      },
    ],
    [filters, isLoading],
  );

  const onRowSelectionChange = permissions.includes("campaigns_full")
    ? setSelectedProfiles
    : undefined;

  const tableData = rows || new Array(pageSize).fill({});

  const table = useDataTable(
    {
      data: tableData,
      columns,
    },
    {
      selectedRows: selectedProfiles,
      onRowSelectionChange,
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
    },
  );

  function onRowClick(row: AudienceProfile) {
    if (!canOpenLeadPreview) {
      return;
    }

    openLeadPreview(row.id);
  }

  if (rows && !rows.length && !isLoading) {
    return emptyStateComponent;
  }

  return (
    <DataTable
      title="Targets"
      table={table}
      onRowClick={onRowClick}
      totalItemCount={totalItemCount}
      isLoading={isLoading}
    />
  );
}
